import React from "react";
import { throttle } from "lodash";

/**
 * Creates a batching queue for state updates that flushes with leading and trailing edge behavior.
 *
 * Uses lodash throttle with both leading and trailing enabled:
 * - First call flushes immediately (leading edge)
 * - Subsequent calls within flushIntervalMs are batched
 * - Final flush happens after flushIntervalMs (trailing edge)
 *
 * Perfect for high-frequency streaming data where you want immediate feedback
 * on first update, batching for performance, and guaranteed final flush.
 *
 * @param setter - React state setter function
 * @param flushIntervalMs - How often to flush the queue in milliseconds (default: 500ms)
 * @param preprocessQueue - Optional function to preprocess the queue before applying updates
 * @returns Batched state setter that queues and flushes updates
 *
 * @example
 * // Basic usage with default queue processing (sequential reduce)
 * const batchedSetPredictions = createBatchedStateSetter(setPredictions, 500);
 *
 * // Advanced usage with custom queue preprocessing for optimization
 * const batchedSetPredictions = createBatchedStateSetter(
 *   setPredictions,
 *   500,
 *   (prevState, updateQueue) => {
 *     // Custom merge logic - only call updater functions when needed
 *     return updateQueue.reduce((state, updater) => updater(state), prevState);
 *   }
 * );
 */
export function createBatchedStateSetter<T, U = (prev: T) => T>({
    setter,
    flushIntervalMs = 500,
    preprocessQueue
}: {
    setter: React.Dispatch<React.SetStateAction<T>>;
    flushIntervalMs: number;
    preprocessQueue?: (prevState: T, updateQueue: U[]) => T;
}): ((updater: React.SetStateAction<T> | U) => void) & {
    cancel: () => void;
    flush: () => void;
    getQueueSize: () => number;
} {
    let updateQueue: U[] = [];
    let hasDirectValue = false;
    let directValue: T;

    function flush() {
        if (updateQueue.length === 0 && !hasDirectValue) return;

        if (hasDirectValue) {
            // If we have a direct value, use it (last direct value wins)
            setter(directValue);
        } else {
            // Use custom preprocessQueue if provided, otherwise default to reduce
            if (preprocessQueue) {
                // Call preprocessQueue once with the entire queue, then set the result
                setter((prevState: T) => {
                    const newState = preprocessQueue(prevState, updateQueue);
                    return newState;
                });
            } else {
                // Default behavior: compose all queued updater functions
                setter((prevState: T) => {
                    return (updateQueue as ((prev: T) => T)[]).reduce(
                        (state, updater) => updater(state),
                        prevState
                    );
                });
            }
        }

        // Clear the queue after flushing
        updateQueue = [];
        hasDirectValue = false;
    }

    // Create throttled flush with leading and trailing edges
    const throttledFlush = throttle(flush, flushIntervalMs, { leading: true, trailing: true });

    function cancel() {
        throttledFlush.cancel();
        updateQueue = [];
        hasDirectValue = false;
    }

    function batchedSetter(updater: React.SetStateAction<T> | U) {
        // Add update to queue
        if (typeof updater === "function" && !preprocessQueue) {
            // Legacy mode: store functions in queue (only when no preprocessQueue provided)
            updateQueue.push(updater as U);
            hasDirectValue = false;
        } else if (typeof updater !== "function" && !preprocessQueue) {
            // Direct state value (legacy mode)
            directValue = updater as T;
            hasDirectValue = true;
            updateQueue = []; // Direct value overrides queued updates
        } else {
            // Data object mode: store the data object directly in queue
            // This handles both function calls (when preprocessQueue exists) and data objects
            updateQueue.push(updater as U);
            hasDirectValue = false;
        }

        // Call throttled flush (leading edge fires immediately, trailing edge fires after interval)
        throttledFlush();
    }

    function getQueueSize() {
        return updateQueue.length + (hasDirectValue ? 1 : 0);
    }

    batchedSetter.cancel = cancel;
    batchedSetter.flush = flush;
    batchedSetter.getQueueSize = getQueueSize;
    return batchedSetter;
}
